import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFriends } from '../context/FriendContext';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useAuth } from '../context/AuthContext';
import { AggregatedCruise } from '../services/friendService';
import { parseISO, startOfDay, isSameDay, differenceInDays } from 'date-fns';
import { isCruiseCurrentlySailing, getCruiseEndTime } from '../utils/dateUtils';
import { motion } from 'framer-motion';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import LazyImage from './LazyImage';
import CustomCruiseIcon from './CustomCruiseIcon';
import {
  Ship,
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Clock,
  UserPlus,
  Waves,
  Compass,
  PartyPopper,
  Navigation,
} from 'lucide-react';
import { FriendsSkeleton } from './LoadingSkeleton';
import { PublicCruiseView } from '../types/friends';
import { getLookupByUid } from '../services/friendLookupService';
import { formatFriendHandle, getFriendInitial } from '../utils/friendDisplayUtils';

interface FriendsFeedProps {
  onAddFriend?: () => void;
}

const FriendsFeed = ({ onAddFriend }: FriendsFeedProps) => {
  const { friends, getAllFriendsCruises, loading: friendsLoading } = useFriends();
  const { cruises: userCruises } = useCruises();
  const { currentUser } = useAuth();
  const { getShipImageUrl, getShipLocationUrl, getShipDeckplanUrl } = useCruiseMetadata();
  const navigate = useNavigate();
  const { formatDate, formatTimeString } = useDisplayPreferences();

  const [aggregatedCruises, setAggregatedCruises] = useState<AggregatedCruise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State to manage skeleton display timing
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // Track when data is actually loaded
  useEffect(() => {
    if (!friendsLoading && !loading && !error) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [friendsLoading, loading, error]);

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    if (dataLoaded) {
      const timer = setTimeout(() => {
        setMinTimeElapsed(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setMinTimeElapsed(false);
    }
  }, [dataLoaded]);

  useEffect(() => {
    let cancelled = false;

    const loadFeedData = async () => {
      try {
        setLoading(true);
        setError(null);
        const friendsCruises = await getAllFriendsCruises();
        if (cancelled) return;

        const now = new Date();
        const userRelevantCruises = userCruises.filter(cruise => {
          // Include cruises that are currently sailing or whose end time is in the future (upcoming)
          return isCruiseCurrentlySailing(cruise, now) || getCruiseEndTime(cruise) > now;
        });

        // Create a map of friends cruises by ship+dates for quick lookup
        const allCruises = [...friendsCruises];

        // Resolve current user's display name from friendLookup so username updates show in the feed
        let currentUserDisplayName: string = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'You';
        if (currentUser) {
          try {
            const lookup = await getLookupByUid(currentUser.uid);
            if (lookup?.username) {
              currentUserDisplayName = formatFriendHandle(lookup.username);
            }
          } catch {
            // keep fallback
          }
        }

        // Check each user cruise - if friends are on it, add user to that group
        // Use same key format as getAllFriendsCruises: cruiseLine|shipName|departureDate|returnDate
        userRelevantCruises.forEach(userCruise => {
          const key = `${userCruise.cruiseLine}|${userCruise.shipName}|${userCruise.departureDate}|${userCruise.returnDate}`;
          
          // Find if any friend cruise matches this user cruise
          const matchingCruiseIndex = allCruises.findIndex(ac => 
            `${ac.cruise.cruiseLine}|${ac.cruise.shipName}|${ac.cruise.departureDate}|${ac.cruise.returnDate}` === key
          );
          
          if (matchingCruiseIndex !== -1 && currentUser) {
            // Friends are on this cruise - add user to the existing group
            const existing = allCruises[matchingCruiseIndex];
            // Check if user is already in the list (avoid duplicates)
            const userExists = existing.friends.some(
              f => f.friendId === currentUser.uid
            );
            if (!userExists) {
              existing.friends.push({
                friendId: currentUser.uid,
                friendDisplayName: currentUserDisplayName,
                friendEmail: currentUser.email || '',
                accessLevel: 'full-access', // User always has full access to their own cruises
              });
            }
          }
        });

        if (!cancelled) setAggregatedCruises(allCruises);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading friends feed:', err);
          setError(err.message || 'Failed to load friends cruises');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Load even if no friends (to show empty state)
    loadFeedData();
    return () => { cancelled = true; };
  }, [friends.length, getAllFriendsCruises, userCruises, currentUser]);

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return <FriendsSkeleton />;
  }

  // Empty state: No friends
  if (friends.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-md p-12 text-center"
      >
        <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <p className="text-lg text-gray-600 mb-2">No friends yet</p>
        <p className="text-sm text-gray-500 mb-6">
          Add friends to start seeing their upcoming cruises in your feed!
        </p>
        <button
          onClick={() => {
            if (onAddFriend) {
              onAddFriend();
            } else {
              navigate('/friends?tab=friends-list');
            }
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
        >
          <UserPlus className="h-5 w-5" />
          Add Your First Friend
        </button>
      </motion.div>
    );
  }

  // Empty state: Friends but no cruises
  if (!loading && aggregatedCruises.length === 0 && !error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-md p-12 text-center"
      >
        <Ship className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <p className="text-lg text-gray-600 mb-2">No upcoming cruises</p>
        <p className="text-sm text-gray-500">
          Your friends don't have any upcoming cruises to share yet.
        </p>
      </motion.div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <div className="text-center">
          <p className="text-red-800 mb-2">Error loading feed</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </motion.div>
    );
  }

  // Helper function to check if a cruise is currently sailing
  // Uses time-aware boundary: port arrival time on return date, or 8:00 AM device-local time as fallback
  const isCurrentlySailing = (cruise: PublicCruiseView): boolean => {
    return isCruiseCurrentlySailing(cruise);
  };

  // Helper function to get current day of cruise
  const getCurrentCruiseDay = (cruise: PublicCruiseView): number => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return differenceInDays(today, departureDate) + 1;
  };

  // Helper function to get total days of cruise
  const getTotalCruiseDays = (cruise: PublicCruiseView): number => {
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return differenceInDays(returnDate, departureDate);
  };

  // Helper function to check if today is departure day
  const isDepartureDay = (cruise: PublicCruiseView): boolean => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return isSameDay(today, departureDate);
  };

  // Helper function to check if today is return day (disembarkation day)
  const isReturnDay = (cruise: PublicCruiseView): boolean => {
    const today = startOfDay(new Date());
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return isSameDay(today, returnDate);
  };

  // Helper function to get today's port
  const getTodaysPort = (cruise: PublicCruiseView) => {
    const today = startOfDay(new Date());
    return cruise.ports?.find(port => {
      if (!port.date) return false;
      const portDate = startOfDay(parseISO(port.date));
      return isSameDay(portDate, today);
    });
  };

  // Helper function to get today's location info
  const getTodaysLocation = (cruise: PublicCruiseView) => {
    if (isDepartureDay(cruise)) {
      return {
        icon: PartyPopper,
        iconColor: 'text-green-500',
        title: 'Boarding Day!',
        subtitle: 'Their cruise adventure begins today',
      };
    }

    const todaysPort = getTodaysPort(cruise);
    if (todaysPort) {
      return {
        icon: Compass,
        iconColor: 'text-blue-500',
        title: todaysPort.name,
        subtitle: formatDate(parseISO(todaysPort.date), { includeWeekday: true, shortFormat: true }),
        arrivalTime: todaysPort.arrivalTime,
        departureTime: todaysPort.departureTime,
      };
    }

    return {
      icon: Waves,
      iconColor: 'text-blue-400',
      title: 'Sea Day',
      subtitle: 'At sea today',
    };
  };

  // Separate currently sailing and upcoming cruises
  const currentlySailingCruises = aggregatedCruises.filter(ac => isCurrentlySailing(ac.cruise));
  const upcomingCruises = aggregatedCruises.filter(ac => !isCurrentlySailing(ac.cruise));

  return (
    <div className="space-y-4">
      {/* Currently Sailing Section */}
      {currentlySailingCruises.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Waves className="h-5 w-5 text-secondary-500" />
            Currently Sailing
          </h2>
          {currentlySailingCruises.map((aggregatedCruise) => {
            const { cruise, friends: cruiseFriends } = aggregatedCruise;
            const shipImageUrl = cruise.imageUrl || getShipImageUrl(cruise.cruiseLine, cruise.shipName);
            const isMultipleFriends = cruiseFriends.length > 1;
            const isUserIncluded = cruiseFriends.some(f => f.friendId === currentUser?.uid);
            const friendCount = isUserIncluded ? cruiseFriends.length - 1 : cruiseFriends.length;
            const isSailing = isCurrentlySailing(cruise);
            const currentDay = getCurrentCruiseDay(cruise);
            const totalDays = getTotalCruiseDays(cruise);
            const todaysLocation = getTodaysLocation(cruise);

            return (
              <motion.div
                key={`${cruise.id}-${cruiseFriends.map(f => f.friendId).join('-')}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-md overflow-hidden border-2 border-blue-300 hover:shadow-lg transition-shadow duration-300"
              >
                {/* Currently Sailing Badge */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Waves className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-semibold min-w-0 break-words">
                      {isUserIncluded
                        ? friendCount > 0
                          ? `You and ${friendCount} ${friendCount === 1 ? 'friend' : 'friends'} are currently sailing`
                          : 'You are currently sailing'
                        : isMultipleFriends
                          ? `${cruiseFriends.length} friends are currently sailing`
                          : 'A friend is currently sailing'}
                    </span>
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap min-w-[5rem] text-center">
                    {isReturnDay(cruise) ? 'Disembarkation Day' : `Day ${currentDay} of ${totalDays}`}
                  </span>
                </div>

                {/* Content: Image and Details - Responsive Layout */}
                <div className="flex flex-col lg:flex-row lg:items-stretch">
                  {/* Ship Image - Left Column on Large Screens */}
                  <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 relative overflow-hidden lg:flex">
                    {shipImageUrl ? (
                      <>
                        <LazyImage
                          src={shipImageUrl}
                          alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                          className="w-full h-full object-cover lg:absolute lg:inset-0"
                        />
                        <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-600"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <CustomCruiseIcon size={136} className="text-white/30" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
                      </>
                    )}
                    
                    {/* Ship Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-xl font-semibold truncate">{cruise.shipName}</h3>
                      <p className="text-sm opacity-90">{cruise.cruiseLine}</p>
                    </div>
                  </div>

                  {/* Cruise Details - Right Column on Large Screens */}
                  <div className="p-4 flex-1 min-h-0 overflow-y-auto">
                  {/* Friends List - Now Clickable Buttons */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4 text-secondary-500" />
                      <span className="text-xs font-medium text-gray-700">
                        {isMultipleFriends ? 'Friends on this cruise:' : 'Friend:'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {cruiseFriends.map((friend) => {
                        const isCurrentUser = friend.friendId === currentUser?.uid;
                        return (
                          <Link
                            key={friend.friendId}
                            to={isCurrentUser ? '/dashboard' : `/friend/${friend.friendId}/cruises`}
                            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white rounded-md px-4 py-2 shadow-sm transition duration-200 font-medium cursor-pointer"
                          >
                            <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-semibold">
                              {getFriendInitial(friend.friendDisplayName ?? friend.friendEmail)}
                            </div>
                            <span className="text-sm font-medium">
                              {friend.friendDisplayName ?? friend.friendEmail}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {/* Today's Location */}
                  {isSailing && (
                    <div className="mb-4 p-3 bg-white rounded-lg shadow-sm">
                      <div className="flex items-start gap-2">
                        <todaysLocation.icon className={`h-5 w-5 ${todaysLocation.iconColor} mt-0.5 flex-shrink-0`} />
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${todaysLocation.iconColor}`}>
                            {todaysLocation.title}
                          </p>
                          <p className="text-xs text-gray-600">{todaysLocation.subtitle}</p>
                          {todaysLocation.arrivalTime && (
                            <p className="text-xs text-gray-500 mt-1">
                              Arrival: {formatTimeString(todaysLocation.arrivalTime)}
                            </p>
                          )}
                          {todaysLocation.departureTime && (
                            <p className="text-xs text-gray-500">
                              Departure: {formatTimeString(todaysLocation.departureTime)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date and Port Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start space-x-2">
                      <CalendarIcon className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(parseISO(cruise.departureDate))}
                        </p>
                        <p className="text-xs text-gray-500">Departure</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {cruise.departurePort}
                        </p>
                        <p className="text-xs text-gray-500">Departure Port</p>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <Clock className="h-4 w-4" />
                    <span>
                      {formatDate(parseISO(cruise.departureDate), { shortFormat: true })} -{' '}
                      {formatDate(parseISO(cruise.returnDate))}
                    </span>
                  </div>

                  {/* Ports of Call - blue theme for currently sailing card */}
                  {cruise.ports && cruise.ports.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-2">Ports of Call:</p>
                      <div className="flex flex-wrap gap-2">
                        {cruise.ports
                          .filter(port => port.name?.trim().toLowerCase() !== cruise.departurePort?.trim().toLowerCase())
                          .map((port) => (
                          <span
                            key={port.id}
                            className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full"
                          >
                            {port.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ship Details / Track Ship Location / Deck Plan */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Link
                      to={`/ship/${encodeURIComponent(cruise.cruiseLine)}/${encodeURIComponent(cruise.shipName)}`}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                    >
                      <Ship className="h-4 w-4 mr-2" />
                      Ship Details
                    </Link>
                    {(() => {
                      const shipLocationUrl = getShipLocationUrl(cruise.cruiseLine, cruise.shipName);
                      const shipDeckplanUrl = getShipDeckplanUrl(cruise.cruiseLine, cruise.shipName);
                      return (
                        <>
                          {shipLocationUrl && (
                            <a
                              href={shipLocationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent"
                            >
                              <Navigation className="h-4 w-4 mr-2" />
                              Track Ship Location
                            </a>
                          )}
                          {shipDeckplanUrl && (
                            <a
                              href={shipDeckplanUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent"
                            >
                              <MapPin className="h-4 w-4 mr-2" />
                              View Deck Plan
                            </a>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Upcoming Cruises Section */}
      {upcomingCruises.length > 0 && (
        <div className="space-y-4">
          {currentlySailingCruises.length > 0 && (
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mt-6">
              <CalendarIcon className="h-5 w-5 text-secondary-500" />
              Upcoming Cruises
            </h2>
          )}
          {upcomingCruises.map((aggregatedCruise) => {
            const { cruise, friends: cruiseFriends } = aggregatedCruise;
            const shipImageUrl = cruise.imageUrl || getShipImageUrl(cruise.cruiseLine, cruise.shipName);
            const isMultipleFriends = cruiseFriends.length > 1;
            const isUserIncluded = cruiseFriends.some(f => f.friendId === currentUser?.uid);
            const friendCount = isUserIncluded ? cruiseFriends.length - 1 : cruiseFriends.length;

        return (
          <motion.div
            key={`${cruise.id}-${cruiseFriends.map(f => f.friendId).join('-')}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
          >
            {/* Content: Image and Details - Responsive Layout */}
            <div className="flex flex-col lg:flex-row lg:items-stretch">
              {/* Ship Image - Left Column on Large Screens */}
              <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 relative overflow-hidden lg:flex">
                {shipImageUrl ? (
                  <>
                    <LazyImage
                      src={shipImageUrl}
                      alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                      className="w-full h-full object-cover lg:absolute lg:inset-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  </>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-600"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CustomCruiseIcon size={136} className="text-white/30" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  </>
                )}
                
                {/* Friends Badge Overlay - only when multiple people on this cruise */}
                {isMultipleFriends && (
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2">
                      <Users className="h-4 w-4 text-secondary-600" />
                      <span className="text-sm font-semibold text-gray-800">
                        {isUserIncluded
                          ? friendCount > 0
                            ? `You and ${friendCount} ${friendCount === 1 ? 'friend' : 'friends'}`
                            : 'You'
                          : `${cruiseFriends.length} friends`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Ship Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="text-xl font-semibold truncate">{cruise.shipName}</h3>
                  <p className="text-sm opacity-90">{cruise.cruiseLine}</p>
                </div>
              </div>

              {/* Cruise Details - Right Column on Large Screens */}
              <div className="p-4 flex-1">
              {/* Friends List - Now Clickable Buttons */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-secondary-500" />
                  <span className="text-xs font-medium text-gray-700">
                    {isMultipleFriends ? 'Friends on this cruise:' : 'Friend:'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                       {cruiseFriends.map((friend) => {
                         const isCurrentUser = friend.friendId === currentUser?.uid;
                         const displayName = friend.friendDisplayName ?? friend.friendEmail;
                         return (
                          <Link
                            key={friend.friendId}
                            to={isCurrentUser ? '/dashboard' : `/friend/${friend.friendId}/cruises`}
                            className="flex items-center gap-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md px-4 py-2 shadow-sm transition duration-200 font-medium cursor-pointer"
                          >
                            <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-semibold">
                              {getFriendInitial(displayName)}
                            </div>
                            <span className="text-sm font-medium">
                              {displayName}
                            </span>
                          </Link>
                        );
                      })}
                </div>
              </div>

              {/* Date and Port Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-start space-x-2">
                  <CalendarIcon className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(parseISO(cruise.departureDate))}
                    </p>
                    <p className="text-xs text-gray-500">Departure</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {cruise.departurePort}
                    </p>
                    <p className="text-xs text-gray-500">Departure Port</p>
                  </div>
                </div>
              </div>

              {/* Date Range */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                <Clock className="h-4 w-4" />
                <span>
                  {formatDate(parseISO(cruise.departureDate), { shortFormat: true })} -{' '}
                  {formatDate(parseISO(cruise.returnDate))}
                </span>
              </div>

              {/* Ports of Call */}
              {cruise.ports && cruise.ports.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-2">Ports of Call:</p>
                  <div className="flex flex-wrap gap-2">
                    {cruise.ports
                      .filter(port => port.name?.trim().toLowerCase() !== cruise.departurePort?.trim().toLowerCase())
                      .map((port) => (
                      <span
                        key={port.id}
                        className="text-xs bg-secondary-100 text-secondary-800 px-2 py-1 rounded-full"
                      >
                        {port.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Ship Details / Track Ship Location / Deck Plan (Upcoming) */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Link
                  to={`/ship/${encodeURIComponent(cruise.cruiseLine)}/${encodeURIComponent(cruise.shipName)}`}
                  className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                >
                  <Ship className="h-4 w-4 mr-2" />
                  Ship Details
                </Link>
                {(() => {
                  const shipLocationUrl = getShipLocationUrl(cruise.cruiseLine, cruise.shipName);
                  const shipDeckplanUrl = getShipDeckplanUrl(cruise.cruiseLine, cruise.shipName);
                  return (
                    <>
                      {shipLocationUrl && (
                        <a
                          href={shipLocationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                        >
                          <Navigation className="h-4 w-4 mr-2" />
                          Track Ship Location
                        </a>
                      )}
                      {shipDeckplanUrl && (
                        <a
                          href={shipDeckplanUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          View Deck Plan
                        </a>
                      )}
                    </>
                  );
                })()}
              </div>
              </div>
            </div>
          </motion.div>
        );
      })}
        </div>
      )}
    </div>
  );
};

export default FriendsFeed;

